import { useEffect, useMemo, useState } from 'react';
import {
  BREAKOUT_DEFAULT_GROUP_SIZE,
  BREAKOUT_DEFAULT_MINUTES,
  BREAKOUT_MAX_MINUTES,
  BREAKOUT_MIN_MINUTES,
  BREAKOUT_WARN_SECONDS,
  breakoutGroupOf,
  planRandomGroups,
  type BreakoutMode,
  type Huddle,
  type HuddleBreakout,
} from '@avslack/shared';
import { useUsers } from '../data/users';
import { ApiRequestError, closeBreakouts, startBreakouts, updateBreakouts } from '../lib/api';
import { Avatar } from './Avatar';
import { Dialog } from './Dialog';
import { Icon } from './Icon';

/**
 * KISCSOPORTOS SZOBAK a hivasban (docs/19-kiscsoportos-szobak.html).
 *
 * A hazigazda az idoszakot idore inditja; a csoportok kulon beszelgetnek, a
 * visszaszamlalo mindenkinek latszik, es lejaratkor mindenki visszater.
 */

/** A beosztando tagok: a bent levo kollegak es a beengedett vendegek (mint a szerveren). */
export function breakoutCandidates(huddle: Huddle): string[] {
  return [
    ...huddle.participants.filter((p) => p.state === 'joined').map((p) => p.user.id),
    ...huddle.guests.filter((g) => g.state === 'admitted').map((g) => g.identity),
  ];
}

/** A tag neve a beosztas kulcsabol: kolléga vagy vendeg. */
function useMemberName(huddle: Huddle): (key: string) => string {
  const { nameOf } = useUsers();
  return (key) => huddle.guests.find((g) => g.identity === key)?.name ?? nameOf(key);
}

function errorText(error: unknown, fallback: string): string {
  return error instanceof ApiRequestError ? error.message : fallback;
}

// ─── Visszaszamlalo ───────────────────────────────────────────────────────────

/** Hatralevo ido ms-ben, a sajat ora szerint; fel masodpercenkent frissul. */
function useRemainingMs(endsAtMs: number | null): number | null {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (endsAtMs === null) return;
    const timer = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(timer);
  }, [endsAtMs]);
  return endsAtMs === null ? null : Math.max(0, endsAtMs - now);
}

function formatRemaining(ms: number): string {
  const total = Math.ceil(ms / 1000);
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

/**
 * A hatralevo ido - nagy, mindenki latja.
 *
 * Az utolso percben figyelmeztet (szinnel ES szoveggel is). Nem ketszer masodpercenkent
 * olvastatjuk fel a kepernyoolvasoval: az `aria-live` csak a figyelmeztetes szovegen van.
 */
export function BreakoutCountdown({
  endsAtMs,
  className = '',
}: {
  endsAtMs: number | null;
  className?: string;
}) {
  const remaining = useRemainingMs(endsAtMs);
  if (remaining === null) return null;
  const warn = remaining <= BREAKOUT_WARN_SECONDS * 1000;

  return (
    <time
      title="Ennyi idő van hátra a kiscsoportos beszélgetésből"
      className={[
        'tabular-nums font-display text-lg font-semibold',
        warn ? 'text-[var(--danger)]' : 'text-ink',
        className,
      ].join(' ')}
    >
      {formatRemaining(remaining)}
    </time>
  );
}

// ─── Sav a hivas tetejen ──────────────────────────────────────────────────────

/**
 * A kiscsoportos idoszak jelzese - MINDENKINEK, a hivas tetejen.
 *
 * Aki csoportban van, az a csoportjat es a tarsait latja; aki a foszobaban (kesve
 * jott, vagy a hazigazda meg nem osztotta be), az azt, hogy a tobbiek kulon beszelgetnek.
 */
export function BreakoutBar({
  huddle,
  breakout,
  endsAtMs,
  meId,
  onHelp,
}: {
  huddle: Huddle;
  breakout: HuddleBreakout;
  endsAtMs: number | null;
  meId: string;
  /** Segitseg kerese a hazigazdatol (csak ha van kitol, es nem o maga a hazigazda). */
  onHelp?: () => void;
}) {
  const [helpSent, setHelpSent] = useState(false);
  useEffect(() => {
    if (!helpSent) return;
    // Egy kerest nem lehet percenkent tobbszor elsutni - de egy idő után újra szabad.
    const timer = window.setTimeout(() => setHelpSent(false), 20_000);
    return () => window.clearTimeout(timer);
  }, [helpSent]);
  const nameFor = useMemberName(huddle);
  const group = breakoutGroupOf(breakout, meId);
  const mates = Object.entries(breakout.assignments)
    .filter(([key, groupNo]) => groupNo === group && key !== meId)
    .map(([key]) => nameFor(key));

  const remaining = useRemainingMs(endsAtMs);
  const warn = remaining !== null && remaining <= BREAKOUT_WARN_SECONDS * 1000;

  return (
    <div className="flex items-center gap-3 border-b border-border-soft bg-accent-soft px-4 py-2">
      <span aria-hidden className="text-base text-accent">
        <Icon name="tagok" />
      </span>
      <div className="min-w-0 flex-1">
        {group > 0 ? (
          <>
            <p className="text-sm font-medium text-ink">Kiscsoport {group}</p>
            <p className="truncate text-2xs text-ink-muted">
              {mates.length > 0 ? `Veled: ${mates.join(', ')}` : 'Egyedül vagy a csoportban.'}
            </p>
          </>
        ) : (
          <>
            <p className="text-sm font-medium text-ink">Kiscsoportos időszak zajlik</p>
            <p className="text-2xs text-ink-muted">
              Te a főszobában vagy, a többiek külön csoportokban beszélgetnek.
            </p>
          </>
        )}
        {/* A figyelmeztetes szovege felolvasodik; maga a visszaszamlalas nem. */}
        <p role="status" className={warn ? 'text-2xs font-medium text-[var(--danger)]' : 'sr-only'}>
          {warn ? 'Hamarosan visszatértek a közös beszélgetésbe.' : ''}
        </p>
      </div>
      {onHelp && group > 0 && (
        <button
          type="button"
          disabled={helpSent}
          onClick={() => {
            onHelp();
            setHelpSent(true);
          }}
          className="shrink-0 rounded-control border border-border bg-surface-raised px-3 py-1.5 text-xs font-semibold text-ink hover:bg-surface-hover disabled:opacity-60"
        >
          {helpSent ? 'Elküldve a házigazdának' : 'Segítség kérése'}
        </button>
      )}
      <BreakoutCountdown endsAtMs={endsAtMs} />
    </div>
  );
}

/**
 * A hazigazdanak: kiscsoportbol erkezett segitsegkeresek, egy kattintassal a
 * csoportba lehet lepni.
 */
export function BreakoutHelpBanner({
  huddle,
  requests,
  onDismiss,
}: {
  huddle: Huddle;
  requests: { from: string; group: number }[];
  onDismiss: (from: string) => void;
}) {
  const nameFor = useMemberName(huddle);
  const [busy, setBusy] = useState(false);
  if (requests.length === 0) return null;

  const join = async (from: string, group: number) => {
    setBusy(true);
    try {
      // A hazigazda a sajat kulcsaval athelyezi magat a csoportba.
      if (huddle.hostId) {
        await updateBreakouts(huddle.id, { move: { memberKey: huddle.hostId, groupNo: group } });
      }
      onDismiss(from);
    } catch {
      // A hazigazda a kezelo panelen is athelyezheti magat.
    } finally {
      setBusy(false);
    }
  };

  return (
    <div role="status" className="space-y-1 border-b border-border-soft bg-accent-soft px-4 py-2">
      {requests.map((request) => (
        <div key={request.from} className="flex items-center gap-3">
          <p className="min-w-0 flex-1 text-sm text-ink">
            <span className="font-medium">{nameFor(request.from)}</span> segítséget kér (
            {request.group}. csoport)
          </p>
          <button
            type="button"
            disabled={busy}
            onClick={() => void join(request.from, request.group)}
            className="shrink-0 rounded-control bg-accent px-3 py-1.5 text-xs font-semibold text-accent-ink hover:bg-accent-hover disabled:opacity-60"
          >
            Belépés a csoportba
          </button>
          <button
            type="button"
            onClick={() => onDismiss(request.from)}
            aria-label="Bezárás"
            className="shrink-0 rounded-control px-2 py-1 text-xs text-ink-muted hover:bg-surface-hover hover:text-ink"
          >
            ×
          </button>
        </div>
      ))}
    </div>
  );
}

// ─── Inditas (hazigazda) ──────────────────────────────────────────────────────

const QUICK_MINUTES = [5, 10, 15, 30];

export function BreakoutStartDialog({
  open,
  onOpenChange,
  huddle,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  huddle: Huddle;
}) {
  const nameFor = useMemberName(huddle);
  const keys = breakoutCandidates(huddle);

  const [minutes, setMinutes] = useState(BREAKOUT_DEFAULT_MINUTES);
  const [mode, setMode] = useState<BreakoutMode>('random');
  const [groupSize, setGroupSize] = useState(BREAKOUT_DEFAULT_GROUP_SIZE);
  /** Kezi beosztas: kulcs -> csoport (0 = a foszobaban marad). */
  const [manual, setManual] = useState<Record<string, number>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Kezi moddra valtva egy veletlen javaslatbol indulunk: azt kell csak igazitani.
  // A javaslat a megnyitaskor es a csoportmeret valtasakor frissul.
  const keysKey = keys.join('|');
  const proposal = useMemo(
    () => planRandomGroups(keysKey ? keysKey.split('|') : [], groupSize),
    [keysKey, groupSize],
  );
  useEffect(() => {
    if (open && mode === 'manual') setManual(proposal);
  }, [open, mode, proposal]);

  const previewGroups = new Set(Object.values(proposal)).size;
  const manualGroups = new Set(Object.values(manual).filter((groupNo) => groupNo > 0)).size;
  const groupChoices = Math.max(3, Math.ceil(keys.length / 2) + 1);

  const tooFew = keys.length < 2;

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      if (mode === 'random') {
        await startBreakouts(huddle.id, { mode, durationMinutes: minutes, groupSize });
      } else {
        const assignments = Object.fromEntries(
          Object.entries(manual).filter(([, groupNo]) => groupNo > 0),
        );
        await startBreakouts(huddle.id, { mode, durationMinutes: minutes, assignments });
      }
      onOpenChange(false);
    } catch (err) {
      setError(errorText(err, 'A kiscsoportok indítása nem sikerült.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Kiscsoportos beszélgetés"
      description="Megadott időre külön csoportokba osztod a résztvevőket. Az idő lejártakor mindenki visszatér."
    >
      <div className="space-y-4 text-sm">
        <fieldset>
          <legend className="mb-1.5 text-xs font-semibold text-ink-muted">Mennyi ideig?</legend>
          <div className="flex flex-wrap items-center gap-2">
            {QUICK_MINUTES.map((value) => (
              <button
                key={value}
                type="button"
                aria-pressed={minutes === value}
                onClick={() => setMinutes(value)}
                className={[
                  'rounded-pill border px-3 py-1 text-xs font-semibold',
                  minutes === value
                    ? 'border-accent bg-accent-soft text-accent'
                    : 'border-border-soft text-ink-muted hover:border-border-strong',
                ].join(' ')}
              >
                {value} perc
              </button>
            ))}
            <label className="flex items-center gap-1.5 text-xs text-ink-muted">
              más:
              <input
                type="number"
                min={BREAKOUT_MIN_MINUTES}
                max={BREAKOUT_MAX_MINUTES}
                value={minutes}
                onChange={(event) =>
                  setMinutes(
                    Math.min(
                      BREAKOUT_MAX_MINUTES,
                      Math.max(BREAKOUT_MIN_MINUTES, Number(event.target.value) || 0),
                    ),
                  )
                }
                className="w-16 rounded-control border border-border bg-surface px-2 py-1 text-xs"
              />
              perc
            </label>
          </div>
        </fieldset>

        <fieldset>
          <legend className="mb-1.5 text-xs font-semibold text-ink-muted">Beosztás</legend>
          <div className="flex gap-2">
            {(['random', 'manual'] as const).map((value) => (
              <button
                key={value}
                type="button"
                aria-pressed={mode === value}
                onClick={() => setMode(value)}
                className={[
                  'rounded-control border px-3 py-1.5 text-xs font-semibold',
                  mode === value
                    ? 'border-accent bg-accent-soft text-accent'
                    : 'border-border-soft text-ink-muted hover:border-border-strong',
                ].join(' ')}
              >
                {value === 'random' ? 'Véletlenszerű' : 'Kézi'}
              </button>
            ))}
          </div>
        </fieldset>

        {mode === 'random' ? (
          <div className="space-y-1">
            <label className="flex items-center gap-2 text-xs text-ink-muted">
              Csoportméret:
              <input
                type="number"
                min={2}
                max={20}
                value={groupSize}
                onChange={(event) =>
                  setGroupSize(Math.min(20, Math.max(2, Number(event.target.value) || 2)))
                }
                className="w-16 rounded-control border border-border bg-surface px-2 py-1 text-xs"
              />
              fős csoportok
            </label>
            <p className="text-2xs text-ink-faint">
              {keys.length} résztvevőből {previewGroups} csoport lesz. Aki egyedül maradna, a
              legutolsó csoporthoz kerül.
            </p>
          </div>
        ) : (
          <div className="space-y-1.5">
            <p className="text-2xs text-ink-faint">
              Javaslatból indulsz, igazíthatod. „Főszoba": nem kerül csoportba ({manualGroups}{' '}
              csoport).
            </p>
            <ul className="max-h-56 space-y-1 overflow-y-auto pr-1">
              {keys.map((key) => (
                <li key={key} className="flex items-center gap-2">
                  <span className="min-w-0 flex-1 truncate">{nameFor(key)}</span>
                  <select
                    value={manual[key] ?? 0}
                    onChange={(event) =>
                      setManual((current) => ({ ...current, [key]: Number(event.target.value) }))
                    }
                    aria-label={`${nameFor(key)} csoportja`}
                    className="rounded-control border border-border bg-surface px-2 py-1 text-xs"
                  >
                    <option value={0}>Főszoba</option>
                    {Array.from({ length: groupChoices }, (_, i) => (
                      <option key={i + 1} value={i + 1}>
                        {i + 1}. csoport
                      </option>
                    ))}
                  </select>
                </li>
              ))}
            </ul>
          </div>
        )}

        {tooFew && (
          <p className="text-xs text-ink-muted">Legalább két résztvevő kell a kiscsoportokhoz.</p>
        )}
        {error && (
          <p role="alert" className="text-xs text-[var(--danger)]">
            {error}
          </p>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="rounded-control px-3 py-2 text-xs text-ink-muted hover:bg-surface-hover"
          >
            Mégsem
          </button>
          <button
            type="button"
            disabled={busy || tooFew || (mode === 'manual' && manualGroups === 0)}
            onClick={() => void submit()}
            className="rounded-control bg-accent px-3 py-2 text-xs font-semibold text-accent-ink hover:bg-accent-hover disabled:opacity-50"
          >
            {busy ? 'Indítás…' : `Indítás (${minutes} perc)`}
          </button>
        </div>
      </div>
    </Dialog>
  );
}

// ─── Kezeles (hazigazda, kozben) ──────────────────────────────────────────────

/**
 * A hazigazda attekintoje a nyitott idoszak alatt: ki hol van, athelyezes,
 * hosszabbitas es a korai lezaras.
 */
export function BreakoutHostPanel({
  huddle,
  breakout,
  onClose,
}: {
  huddle: Huddle;
  breakout: HuddleBreakout;
  onClose: () => void;
}) {
  const nameFor = useMemberName(huddle);
  const { usersById } = useUsers();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const members = breakoutCandidates(huddle);
  const groupNos = [...new Set(Object.values(breakout.assignments))].sort((a, b) => a - b);
  const lobby = members.filter((key) => !breakout.assignments[key]);
  const choices = Math.max(groupNos.length + 1, 3);

  const run = async (action: () => Promise<unknown>, fallback: string) => {
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (err) {
      setError(errorText(err, fallback));
    } finally {
      setBusy(false);
    }
  };

  const move = (memberKey: string, groupNo: number) =>
    run(
      () => updateBreakouts(huddle.id, { move: { memberKey, groupNo } }),
      'Az áthelyezés nem sikerült.',
    );

  const avatarOf = (key: string) => {
    const user = usersById.get(key);
    return (
      <Avatar userId={key} name={nameFor(key)} avatarUrl={user?.avatarUrl ?? null} size="xs" />
    );
  };

  const row = (key: string) => (
    <li key={key} className="flex items-center gap-2">
      {avatarOf(key)}
      <span className="min-w-0 flex-1 truncate text-xs">{nameFor(key)}</span>
      <select
        value={breakout.assignments[key] ?? 0}
        disabled={busy}
        onChange={(event) => {
          const next = Number(event.target.value);
          if (next > 0) void move(key, next);
        }}
        aria-label={`${nameFor(key)} áthelyezése`}
        className="rounded-control border border-border bg-surface px-1.5 py-0.5 text-2xs"
      >
        {!breakout.assignments[key] && <option value={0}>Főszoba</option>}
        {Array.from({ length: choices }, (_, i) => (
          <option key={i + 1} value={i + 1}>
            {i + 1}. csoport
          </option>
        ))}
      </select>
    </li>
  );

  return (
    <aside className="flex shrink-0 flex-col max-sm:absolute max-sm:inset-0 max-sm:z-30 max-sm:bg-surface-raised border-l border-border-soft sm:w-80">
      <header className="flex items-center gap-2 border-b border-border-soft px-3 py-2">
        <p className="min-w-0 flex-1 font-display text-sm font-semibold">Kiscsoportok kezelése</p>
        <button
          type="button"
          onClick={onClose}
          aria-label="Bezárás"
          className="rounded-control px-2 py-1 text-xs text-ink-muted hover:bg-surface-hover hover:text-ink"
        >
          ×
        </button>
      </header>

      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-3">
        {groupNos.map((groupNo) => (
          <section key={groupNo}>
            <h3 className="mb-1 text-2xs font-semibold uppercase tracking-wide text-ink-faint">
              {groupNo}. csoport
            </h3>
            <ul className="space-y-1">
              {members.filter((key) => breakout.assignments[key] === groupNo).map(row)}
            </ul>
          </section>
        ))}

        {lobby.length > 0 && (
          <section>
            <h3 className="mb-1 text-2xs font-semibold uppercase tracking-wide text-ink-faint">
              Főszoba (nincs csoportban)
            </h3>
            <ul className="space-y-1">{lobby.map(row)}</ul>
          </section>
        )}

        {error && (
          <p role="alert" className="text-xs text-[var(--danger)]">
            {error}
          </p>
        )}
      </div>

      <footer className="flex flex-wrap gap-2 border-t border-border-soft p-3">
        <button
          type="button"
          disabled={busy}
          onClick={() =>
            void run(
              () => updateBreakouts(huddle.id, { extendMinutes: 5 }),
              'A hosszabbítás nem sikerült.',
            )
          }
          className="rounded-control border border-border px-3 py-1.5 text-xs font-semibold hover:bg-surface-hover disabled:opacity-50"
        >
          +5 perc
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => void run(() => closeBreakouts(huddle.id), 'A lezárás nem sikerült.')}
          className="rounded-control bg-[var(--danger)] px-3 py-1.5 text-xs font-semibold text-white hover:opacity-90 disabled:opacity-50"
        >
          Befejezés most
        </button>
      </footer>
    </aside>
  );
}
